import React from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, GraduationCap, Briefcase, Building2, ArrowUpRight } from "lucide-react";
import {
  Container,
  SectionHeading,
  Overline,
  Reveal,
  GlassCard,
  AxorButton,
  EASE,
} from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import { PrismBackground, FloatingShard } from "@/components/Prism";
import { ContactForm } from "@/components/ContactForm";
import { LocationBlock } from "@/components/LocationBlock";
import {
  AXOR_EMAIL,
  AXOR_PHONE,
  telLink,
  mailtoLink,
  waLink,
  WHATSAPP_MESSAGES,
} from "@/config";

const pathways = [
  {
    icon: GraduationCap,
    title: "Students & Graduates",
    copy: "Ready to take the next step?",
    cta: "Chat With Axor",
    href: waLink(WHATSAPP_MESSAGES.student),
    testid: "pathway-students",
  },
  {
    icon: Briefcase,
    title: "Professionals & Green Card Holders",
    copy: "Looking for your next opportunity?",
    cta: "Explore Opportunities",
    href: waLink(WHATSAPP_MESSAGES.professional),
    testid: "pathway-professionals",
  },
  {
    icon: Building2,
    title: "Employers",
    copy: "Looking for exceptional talent?",
    cta: "Talk To Axor",
    href: waLink(WHATSAPP_MESSAGES.employer),
    testid: "pathway-employers",
  },
];

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
  </svg>
);

export default function Contact() {
  return (
    <>
      <PageHero
        overline="Contact"
        title="Let's Start A Conversation."
        highlight="Conversation"
        description="Whether you're exploring your next career opportunity, seeking professional guidance, or looking for exceptional talent, we're here to help."
        testid="contact-hero"
      />

      {/* Audience pathways */}
      <section className="relative overflow-hidden py-20 md:py-28" data-testid="contact-pathways">
        <PrismBackground />
        <Container className="relative">
          <SectionHeading overline="Choose Your Path" title="How Can We Help You?" highlight="Help You" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {pathways.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.1 }}
              >
                <GlassCard className="group flex h-full flex-col p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-axor-cyan/25 bg-axor-cyan/10 text-axor-cyan transition-transform duration-300 group-hover:scale-110">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-axor-white">{p.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-axor-text">{p.copy}</p>
                  <AxorButton
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    icon={ArrowUpRight}
                    className="mt-6 w-full"
                    data-testid={p.testid}
                  >
                    {p.cta}
                  </AxorButton>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Contact info + form */}
      <section className="relative overflow-hidden bg-axor-deep py-20 md:py-28" data-testid="contact-info">
        <PrismBackground />
        <Container className="relative grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <Reveal><Overline>Get In Touch</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-axor-white sm:text-4xl">
                Axor Technologies
              </h2>
            </Reveal>
            <div className="mt-8 space-y-4">
              <a
                href={mailtoLink()}
                className="group flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 transition-colors hover:border-axor-cyan/25"
                data-testid="contact-email-link"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-axor-cyan/10 text-axor-cyan">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.16em] text-axor-text">Email</span>
                  <span className="block text-base font-medium text-axor-white group-hover:text-axor-cyan">{AXOR_EMAIL}</span>
                </span>
              </a>
              <a
                href={telLink}
                className="group flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 transition-colors hover:border-axor-cyan/25"
                data-testid="contact-phone-link"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-axor-cyan/10 text-axor-cyan">
                  <Phone className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.16em] text-axor-text">Phone</span>
                  <span className="block text-base font-medium text-axor-white group-hover:text-axor-cyan">{AXOR_PHONE}</span>
                </span>
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <AxorButton href={mailtoLink()} icon={Mail} data-testid="contact-email-btn">Email Us</AxorButton>
              <AxorButton href={telLink} variant="secondary" icon={Phone} data-testid="contact-call-btn">Call Axor</AxorButton>
              <AxorButton href={waLink()} target="_blank" rel="noopener noreferrer" variant="secondary" icon={WhatsAppGlyph} data-testid="contact-whatsapp-btn">
                Chat On WhatsApp
              </AxorButton>
            </div>
          </div>

          <div className="relative">
            <FloatingShard className="-left-4 -top-4 h-8 w-8 z-10" shape="diamond" delay={0.3} />
            <ContactForm />
          </div>
        </Container>
      </section>

      {/* Location */}
      <section className="relative overflow-hidden py-20 md:py-28" data-testid="contact-location">
        <PrismBackground />
        <Container className="relative">
          <SectionHeading overline="Visit Us" title="Where To Find Axor." highlight="Find Axor" />
          <div className="mt-12">
            <LocationBlock />
          </div>
        </Container>
      </section>
    </>
  );
}
